import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Empty,
  Input,
  List,
  message,
  Select,
  Skeleton,
  Space,
  Statistic,
  Tag,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  FileDoneOutlined,
  FilterOutlined,
  IdcardOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  ShareAltOutlined,
  SolutionOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import JobDetailsDrawer from "components/jobs/JobDetailsDrawer";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { OLIVEEB_COLORS } from "workspaces/colors";
import "./JobSearch.css";

const { Paragraph, Text, Title } = Typography;

const PAGE_SIZE = 20;
const queryParam = (search, key) => new URLSearchParams(search).get(key);
const DEFAULT_FILTERS = {
  locationType: undefined,
  workSchedules: [],
  requiredEducationLevel: undefined,
  requiredYearsOfExperience: undefined,
};
const APPLIED_FILTERS = [
  { value: "ALL", label: "All" },
  { value: "APPLIED", label: "Applied" },
  { value: "UNAPPLIED", label: "Unapplied" },
];

const LOCATION_TYPES = [
  { value: "REMOTE", label: "Remote" },
  { value: "ONSITE", label: "On-site" },
  { value: "HYBRID", label: "Hybrid" },
];

const WORK_SCHEDULES = [
  { value: "FULL_TIME", label: "Full time" },
  { value: "PART_TIME", label: "Part time" },
  { value: "CONTRACT", label: "Contract" },
  { value: "TEMPORARY", label: "Temporary" },
  { value: "FLEXIBLE", label: "Flexible" },
];

const EDUCATION_LEVELS = [
  { value: "HIGH_SCHOOL", label: "High school" },
  { value: "CERTIFICATE", label: "Certificate" },
  { value: "ASSOCIATE", label: "Associate" },
  { value: "BACHELOR", label: "Bachelor" },
  { value: "MASTER", label: "Master" },
];

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isUuid = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim(),
  );

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const displayText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim() && !isUuid(value)) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const namedRefLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string") return isUuid(value) ? "" : titleCase(value);
  return displayText(value.name, value.label, value.title, value.code);
};

const normalizeStrings = (values) => {
  if (!values) return [];
  const list = Array.isArray(values) ? values : Array.from(values instanceof Set ? values : [values]);
  return list
    .map((value) => {
      if (value && typeof value === "object") return namedRefLabel(value);
      return titleCase(value);
    })
    .filter(Boolean);
};

const asList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value instanceof Set) return Array.from(value);
  return [value];
};

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
};

const pageContent = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const formatMoney = (value, currency = "USD") => {
  if (value === undefined || value === null || String(value).trim() === "") return "";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    }).format(numeric);
  } catch {
    return `$${numeric.toLocaleString()}`;
  }
};

const salaryLabel = (job) => {
  const direct = firstValue(
    job?.salaryExpectation,
    job?.salaryExpectations,
    job?.salaryRange,
    job?.compensation,
    job?.pay,
    job?.payRange,
  );
  if (direct) return String(direct);

  const currency = job?.currency || job?.salaryCurrency || job?.payCurrency || "USD";
  const min = firstValue(job?.salaryMin, job?.minSalary, job?.minimumSalary, job?.payMin, job?.minPay, job?.rateMin);
  const max = firstValue(job?.salaryMax, job?.maxSalary, job?.maximumSalary, job?.payMax, job?.maxPay, job?.rateMax);
  const single = firstValue(job?.salary, job?.payRate, job?.hourlyRate, job?.rate);
  const cadence = titleCase(firstValue(job?.salaryPeriod, job?.payPeriod, job?.rateUnit, job?.payFrequency));
  const suffix = cadence ? ` / ${cadence.toLowerCase()}` : "";

  if (min && max) return `${formatMoney(min, currency)} - ${formatMoney(max, currency)}${suffix}`;
  if (min) return `From ${formatMoney(min, currency)}${suffix}`;
  if (max) return `Up to ${formatMoney(max, currency)}${suffix}`;
  if (single) return `${formatMoney(single, currency)}${suffix}`;
  return "";
};

const locationLabel = (location) => {
  if (!location) return "";
  const direct = displayText(location.label, location.name, location.address, location.formattedAddress);
  if (direct) return direct;
  return [location.city, location.state, location.country].filter(Boolean).join(", ");
};

const formatDate = (value) => {
  if (!value) return "Recently";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatDateTime = (value) => {
  if (!value) return "Recently";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const applicationJobOpportunityId = (item) =>
  firstText(item?.jobOpportunityId, item?.jobPostId, item?.jobOpportunity?.id, item?.jobPost?.id);

const postId = (post) => firstText(post?.id, post?.jobOpportunityId);

const statusColor = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "OPEN" || normalized === "ACTIVE") return "green";
  if (normalized === "DRAFT") return "gold";
  if (normalized === "CLOSED" || normalized === "ARCHIVED") return "red";
  return "blue";
};

const roleOptionsFor = (roles, query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [];

  return roles
    .map((role, index) => {
      const title = String(role.title || "").toLowerCase();
      const synonyms = Array.isArray(role.synonyms)
        ? role.synonyms.map((value) => String(value || "").toLowerCase())
        : [];
      const description = String(role.description || "").toLowerCase();
      let rank = Number.POSITIVE_INFINITY;

      if (title === normalizedQuery) rank = 0;
      else if (title.startsWith(normalizedQuery)) rank = 1;
      else if (title.includes(normalizedQuery)) rank = 2;
      else if (synonyms.some((value) => value === normalizedQuery)) rank = 3;
      else if (synonyms.some((value) => value.startsWith(normalizedQuery))) rank = 4;
      else if (synonyms.some((value) => value.includes(normalizedQuery))) rank = 5;
      else if (description.includes(normalizedQuery)) rank = 6;

      return { role, index, rank };
    })
    .filter((item) => Number.isFinite(item.rank))
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        String(a.role.title || "").localeCompare(String(b.role.title || "")) ||
        a.index - b.index,
    )
    .slice(0, 8)
    .map(({ role }) => ({
      value: role.title,
      label: (
        <Space>
          <SolutionOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} />
          <span>{role.title}</span>
        </Space>
      ),
    }));
};

const canApplyToJob = (job) =>
  !job?.applied && String(job?.status || "OPEN").toUpperCase() === "OPEN";

const JobApplyAction = ({ job, onApply, block = false }) => {
  if (job?.applied) {
    return (
      <Button
        className={[
          "job-search-apply-button",
          "job-search-apply-button--applied",
          block ? "job-search-apply-button--block" : "",
        ].filter(Boolean).join(" ")}
        icon={<CheckCircleOutlined />}
        disabled
      >
        Already Applied
      </Button>
    );
  }

  if (!canApplyToJob(job)) {
    return (
      <Button
        className={[
          "job-search-apply-button",
          "job-search-apply-button--closed",
          block ? "job-search-apply-button--block" : "",
        ].filter(Boolean).join(" ")}
        disabled
      >
        Applications Closed
      </Button>
    );
  }

  return (
    <Button
      type="primary"
      className={[
        "job-search-apply-button",
        block ? "job-search-apply-button--block" : "",
      ].filter(Boolean).join(" ")}
      icon={<SendOutlined />}
      onClick={onApply}
    >
      Apply
    </Button>
  );
};

export const JobCard = ({ job, onOpen, onApply, onOpenDetails, onShare }) => {
  const locationLabels = Array.isArray(job.requiredLocations)
    ? job.requiredLocations.map(locationLabel).filter(Boolean)
    : [];
  const workSchedules = normalizeStrings(job.workSchedules);
  const skillLabels = normalizeStrings([
    ...asList(job.skills),
    ...asList(job.requiredSkills),
    ...asList(job.otherRequiredSkills),
  ]);
  const specializationLabels = normalizeStrings([
    ...asList(job.specializations),
    ...asList(job.requiredSpecializations),
    ...asList(job.otherRequiredSpecializations),
  ]);
  const salary = salaryLabel(job);
  const detailItems = [
    { label: "Location type", value: titleCase(job.locationType), icon: <EnvironmentOutlined /> },
    { label: "Schedule", value: workSchedules.slice(0, 2).join(", "), icon: <CalendarOutlined /> },
    { label: "Salary", value: salary, icon: <DollarOutlined /> },
    {
      label: "Experience",
      value: job.requiredYearsOfExperience != null ? `${job.requiredYearsOfExperience}+ years` : "",
      icon: <FilterOutlined />,
    },
    { label: "Education", value: titleCase(job.requiredEducationLevel), icon: <SolutionOutlined /> },
  ].filter((item) => item.value);
  const chips = [...skillLabels, ...specializationLabels].slice(0, 4);

  return (
    <Card className="job-search-card" hoverable onClick={onOpen}>
      <div className="job-search-card__top">
        <span className="job-search-card__icon">
          <SolutionOutlined />
        </span>
        <div className="job-search-card__title-wrap">
          <Title level={4}>{job.title || "Untitled job post"}</Title>
        </div>
        <Tag color={statusColor(job.status)}>{titleCase(job.status || "OPEN")}</Tag>
      </div>

      <div className="job-search-card__priority-meta">
        <span>
          <ThunderboltOutlined /> Posted {formatDateTime(job.createdAt)}
        </span>
        <span>
          <EnvironmentOutlined /> {locationLabels[0] || titleCase(job.locationType) || "Location not set"}
        </span>
      </div>

      {detailItems.length ? (
        <div className="job-search-card__details">
          {detailItems.map((item) => (
            <div key={item.label} className="job-search-card__detail">
              <Text className="job-search-card__detail-label">{item.label}</Text>
              <strong>{item.icon ? <span className="job-search-card__detail-icon">{item.icon}</span> : null}{item.value}</strong>
            </div>
          ))}
          <div className="job-search-card__actions">
            <Button
              className="job-search-details-button"
              icon={<FileDoneOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                if (onOpen) onOpen(job);
              }}
            >
              Details
            </Button>
            {onShare ? (
              <Button
                className="job-search-share-button"
                icon={<ShareAltOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onShare(job);
                }}
              >
                Share
              </Button>
            ) : null}
            <JobApplyAction
              job={job}
              onApply={(event) => {
                event.stopPropagation();
                onApply(job);
              }}
            />
          </div>
        </div>
      ) : null}

      {chips.length ? (
        <div className="job-search-card__skills">
          <Text className="job-search-card__skills-label">Required skills</Text>
          <Space wrap className="job-search-card__chips">
            {chips.map((chip) => (
              <Tag key={chip}>{chip}</Tag>
            ))}
            {skillLabels.length + specializationLabels.length > chips.length ? (
              <Tag>+{skillLabels.length + specializationLabels.length - chips.length} more</Tag>
            ) : null}
          </Space>
        </div>
      ) : null}

    </Card>
  );
};

const JobSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isProfessionalWorkspace, selectedWorkspaceId } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const initialSearchText = useMemo(
    () => queryParam(location.search, "q") || queryParam(location.search, "query") || "",
    [location.search],
  );
  const [jobs, setJobs] = useState([]);
  const [lastPage, setLastPage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState(initialSearchText);
  const [submittedSearch, setSubmittedSearch] = useState(initialSearchText.trim());
  const [appliedFilter, setAppliedFilter] = useState("ALL");
  const [professionRoles, setProfessionRoles] = useState([]);
  const [professionProfiles, setProfessionProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [selectedJob, setSelectedJob] = useState(null);
  const loadingRef = useRef(false);
  const pageRef = useRef(0);
  const openedJobParamRef = useRef("");
  const submittedSearchRef = useRef(initialSearchText.trim());
  const jobOpportunityIdParam = useMemo(
    () => queryParam(location.search, "jobOpportunityId"),
    [location.search],
  );

  useEffect(() => {
    let cancelled = false;
    olreadiaService.professionRoles
      .list(actorId)
      .then((roles) => {
        if (!cancelled) setProfessionRoles(roles);
      })
      .catch(() => {
        if (!cancelled) setProfessionRoles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [actorId]);

  useEffect(() => {
    let cancelled = false;

    const loadProfessionProfiles = async () => {
      if (!actorId || !selectedWorkspaceId || !isProfessionalWorkspace) {
        setProfessionProfiles([]);
        return;
      }

      setProfilesLoading(true);
      try {
        const result = await olreadiaService.professionProfiles.listForUser(actorId, {
          workspaceId: selectedWorkspaceId,
          page: 0,
          size: 20,
        });
        if (!cancelled) setProfessionProfiles(pageContent(result));
      } catch {
        if (!cancelled) setProfessionProfiles([]);
      } finally {
        if (!cancelled) setProfilesLoading(false);
      }
    };

    loadProfessionProfiles();
    return () => {
      cancelled = true;
    };
  }, [actorId, isProfessionalWorkspace, selectedWorkspaceId]);

  const markApplied = useCallback(
    async (content) => {
      if (!actorId || !selectedWorkspaceId) return content;

      try {
        const applications = await olreadiaService.jobApplications.listMine({
          actorId,
          applicantId: actorId,
          workspaceId: selectedWorkspaceId,
          page: 0,
          size: 1000,
        });
        const appliedIds = new Set((applications.content || []).map(applicationJobOpportunityId).filter(Boolean));
        if (!appliedIds.size) return content;
        return content.map((job) => (appliedIds.has(postId(job)) ? { ...job, applied: true } : job));
      } catch {
        return content;
      }
    },
    [actorId, selectedWorkspaceId],
  );

  const loadJobs = useCallback(
    async ({ reset = false, searchOverride, filtersOverride } = {}) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      const pageToLoad = reset ? 0 : pageRef.current;
      const searchForRequest = searchOverride !== undefined ? searchOverride : submittedSearchRef.current;
      const filtersForRequest = filtersOverride || filters;

      try {
        const result = await olreadiaService.jobOpportunities.listRecent({
          actorId,
          search: searchForRequest,
          locationType: filtersForRequest.locationType,
          workSchedules: filtersForRequest.workSchedules,
          requiredEducationLevel: filtersForRequest.requiredEducationLevel,
          requiredYearsOfExperience: filtersForRequest.requiredYearsOfExperience,
          page: pageToLoad,
          size: PAGE_SIZE,
        });
        const markedContent = await markApplied(result.content || []);
        setJobs((current) => {
          const merged = reset ? markedContent : [...current, ...markedContent];
          const seen = new Set();
          return merged.filter((job) => {
            const key = postId(job) || `${job.title}-${job.createdAt}-${job.updatedAt}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });
        pageRef.current = reset ? 1 : pageToLoad + 1;
        setLastPage(Boolean(result.last));
        setError("");
      } catch (loadError) {
        if (reset) setJobs([]);
        setError(loadError?.message || "Failed to load recent jobs.");
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [actorId, filters, markApplied],
  );

  useEffect(() => {
    loadJobs({ reset: true });
  }, [filters, loadJobs]);

  useEffect(() => {
    let cancelled = false;
    const openLinkedJob = async () => {
      if (!jobOpportunityIdParam || openedJobParamRef.current === jobOpportunityIdParam) return;
      openedJobParamRef.current = jobOpportunityIdParam;
      const existingJob = jobs.find((job) => postId(job) === jobOpportunityIdParam);
      if (existingJob) {
        setSelectedJob(existingJob);
        return;
      }
      try {
        const job = await olreadiaService.jobOpportunities.get(jobOpportunityIdParam, actorId);
        if (!cancelled) setSelectedJob(job);
      } catch {
        if (!cancelled) message.error("Could not open the original job post.");
      }
    };
    openLinkedJob();
    return () => {
      cancelled = true;
    };
  }, [actorId, jobOpportunityIdParam, jobs]);

  const visibleJobs = useMemo(() => {
    if (appliedFilter === "APPLIED") return jobs.filter((job) => Boolean(job.applied));
    if (appliedFilter === "UNAPPLIED") return jobs.filter((job) => !job.applied);
    return jobs;
  }, [appliedFilter, jobs]);

  const remoteCount = useMemo(
    () => visibleJobs.filter((job) => String(job.locationType || "").toUpperCase() === "REMOTE").length,
    [visibleJobs],
  );

  const roleOptions = useMemo(
    () => roleOptionsFor(professionRoles, searchText),
    [professionRoles, searchText],
  );

  const clearFilters = () => {
    submittedSearchRef.current = "";
    setSearchText("");
    setSubmittedSearch("");
    setAppliedFilter("ALL");
    setFilters(DEFAULT_FILTERS);
    loadJobs({ reset: true, searchOverride: "", filtersOverride: DEFAULT_FILTERS });
  };

  const submitSearch = () => {
    const nextSearch = searchText.trim();
    submittedSearchRef.current = nextSearch;
    setSubmittedSearch(nextSearch);
    loadJobs({ reset: true, searchOverride: nextSearch });
  };

  const handleApply = (job) => {
    if (!job) return;
    if (job.applied) return;
    const id = postId(job);
    const url = id ? `${paths.jobApplications}?jobOpportunityId=${encodeURIComponent(id)}` : paths.jobApplications;
    navigate(url);
  };

  const jobDetailsPath = (job) => {
    const id = postId(job);
    return id ? paths.jobOpportunityDetails(encodeURIComponent(id)) : "";
  };

  const openJobDetailsPage = (job) => {
    const url = jobDetailsPath(job);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareJob = async (job) => {
    const path = jobDetailsPath(job);
    if (!path) {
      message.warning("This job is missing a shareable details link.");
      return;
    }
    const url = `${window.location.origin}${path}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: job?.title || "Olreadia job opportunity",
          url,
        });
        return;
      }
      await navigator.clipboard.writeText(url);
      message.success("Job details link copied.");
    } catch (error) {
      if (error?.name !== "AbortError") {
        message.error(error?.message || "Unable to share this job right now.");
      }
    }
  };

  return (
    <div className="job-search-page">
      <div className="job-search-body">
        <div className="job-search-results">
          <section className="job-search-hero">
            <div>
              <Text className="job-search-hero__eyebrow">Recent opportunities</Text>
              <Title level={2}>Jobs Stream</Title>
              <Paragraph>
                Search open opportunities, spot remote roles, and track which jobs you have already applied to.
              </Paragraph>
            </div>
            <Space className="job-search-hero__stats" size={12}>
              <Card>
                <Statistic title="Recent jobs" value={loading && !jobs.length ? "..." : visibleJobs.length} />
              </Card>
              <Card>
                <Statistic title="Remote" value={loading && !jobs.length ? "..." : remoteCount} />
              </Card>
            </Space>
          </section>

          <Card className="job-search-controls">
            <div className="job-search-controls__primary">
              <AutoComplete
                className="job-search-controls__search"
                value={searchText}
                options={roleOptions}
                onSelect={(value) => setSearchText(value)}
                onChange={setSearchText}
              >
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="Search jobs e.g. caregiver, CPR, remote"
                  onPressEnter={submitSearch}
                />
              </AutoComplete>
              <Space wrap>
                <Button type="primary" icon={<SearchOutlined />} onClick={submitSearch} loading={loading}>
                  Search
                </Button>
                {APPLIED_FILTERS.map((filter) => (
                  <Button
                    key={filter.value}
                    type={appliedFilter === filter.value ? "primary" : "default"}
                    onClick={() => setAppliedFilter(filter.value)}
                    className={appliedFilter === filter.value ? "job-search-filter-active" : ""}
                  >
                    {filter.label}
                  </Button>
                ))}
              </Space>
            </div>

            <div className="job-search-controls__advanced">
              <Select
                allowClear
                placeholder="Location type"
                value={filters.locationType}
                options={LOCATION_TYPES}
                onChange={(value) => setFilters((current) => ({ ...current, locationType: value }))}
              />
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                placeholder="Work schedule"
                value={filters.workSchedules}
                options={WORK_SCHEDULES}
                onChange={(value) => setFilters((current) => ({ ...current, workSchedules: value }))}
              />
              <Select
                allowClear
                placeholder="Education"
                value={filters.requiredEducationLevel}
                options={EDUCATION_LEVELS}
                onChange={(value) => setFilters((current) => ({ ...current, requiredEducationLevel: value }))}
              />
              <Input
                type="number"
                min={0}
                placeholder="Years of experience"
                value={filters.requiredYearsOfExperience}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    requiredYearsOfExperience: event.target.value || undefined,
                  }))
                }
              />
              <Button icon={<CloseCircleOutlined />} onClick={clearFilters}>
                Clear
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => loadJobs({ reset: true })} loading={loading}>
                Refresh
              </Button>
            </div>
          </Card>

          {error ? (
            <Alert
              type="error"
              showIcon
              message="Unable to load jobs"
              description={error}
              action={<Button onClick={() => loadJobs({ reset: true })}>Retry</Button>}
              className="job-search-alert"
            />
          ) : null}

          {isProfessionalWorkspace && !profilesLoading && !professionProfiles.length ? (
            <Card className="job-search-create-profile">
              <div className="job-search-create-profile__icon">
                <IdcardOutlined />
              </div>
              <div className="job-search-create-profile__copy">
                <Text className="job-search-create-profile__eyebrow">Professional profile needed</Text>
                <Title level={4}>Create a reusable profile before you apply</Title>
                <Paragraph>
                  Add your skills, experience, documents, services, and locations once so employers can review a trusted profile faster.
                </Paragraph>
              </div>
              <Button type="primary" icon={<SolutionOutlined />} onClick={() => navigate(paths.professionalProfileCreate)}>
                Create profile
              </Button>
            </Card>
          ) : null}

          <List
            className="job-search-list"
            loading={loading && !jobs.length}
            dataSource={visibleJobs}
            renderItem={(job) => (
              <List.Item>
                <JobCard
                  job={job}
                  onOpen={() => setSelectedJob(job)}
                  onApply={handleApply}
                  onShare={shareJob}
                />
              </List.Item>
            )}
            locale={{
              emptyText: loading ? (
                <Skeleton active />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    appliedFilter !== "ALL"
                      ? `No ${appliedFilter.toLowerCase()} jobs found`
                      : submittedSearch
                        ? "No jobs match your search"
                        : "No recent jobs yet"
                  }
                >
                  <Button icon={<FilterOutlined />} onClick={clearFilters}>
                    Reset search
                  </Button>
                </Empty>
              ),
            }}
          />

          {!lastPage && visibleJobs.length ? (
            <div className="job-search-load-more">
              <Button type="primary" onClick={() => loadJobs({ reset: false })} loading={loading}>
                Load more jobs
              </Button>
            </div>
          ) : null}
        </div>

        <SidePromotionSection className="job-search-promotions" showControls={false} />
      </div>

      <JobDetailsDrawer
        job={selectedJob}
        open={Boolean(selectedJob)}
        onClose={() => setSelectedJob(null)}
        onApply={handleApply}
        onOpenDetails={openJobDetailsPage}
        onShare={shareJob}
      />
    </div>
  );
};

export default JobSearch;
